import { Alert, type AlertRootProps } from "@heroui/react";
import { type AppAlertStatus } from "./variants";

/**
 * Constrained alert root props.
 * className escape hatch available for one-off overrides.
 */
export type AppAlertProps = Omit<AlertRootProps, "className" | "status"> & {
  /** Alert severity status. @default "default" */
  status?: AppAlertStatus;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppAlert — Design system alert / notification banner.
 *
 * Wraps HeroUI v3 Alert with constrained Rosé Pine status colors.
 * Sub-components (Indicator, Content, Title, Description) are
 * HeroUI pass-throughs — they inherit slot styling from AlertContext.
 *
 * @example
 * ```tsx
 * <AppAlert status="success">
 *   <AppAlert.Indicator />
 *   <AppAlert.Content>
 *     <AppAlert.Title>Success</AppAlert.Title>
 *     <AppAlert.Description>Operation completed.</AppAlert.Description>
 *   </AppAlert.Content>
 * </AppAlert>
 * ```
 */
function AppAlertRoot({
  status = "default",
  className,
  ...props
}: AppAlertProps) {
  return <Alert {...props} status={status} className={className} />;
}

export const AppAlert = Object.assign(AppAlertRoot, {
  Indicator: Alert.Indicator,
  Content: Alert.Content,
  Title: Alert.Title,
  Description: Alert.Description,
});
